import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Settings, Check, X, Eye, EyeOff } from 'lucide-react';
import './FreeConfigAdmin.css';

interface SubdeckFreeConfig {
    enabled: boolean;
    freeCardsLimit: number;
    freeApostilaPagesLimit: number;
}

interface SubdeckConfig {
    _id: string;
    name: string;
    freeConfig: SubdeckFreeConfig;
}

interface DeckConfig {
    _id: string;
    name: string;
    isCustom: boolean;
    allowFreeEntry: boolean;
    cycle: { _id: string; name: string } | null;
    subdecks: SubdeckConfig[];
}

interface FreeConfigAdminProps {
    onShowToast: (message: string, type: 'success' | 'error') => void;
}

const FreeConfigAdmin: React.FC<FreeConfigAdminProps> = ({ onShowToast }) => {
    const [decks, setDecks] = useState<DeckConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedDecks, setExpandedDecks] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Estado local de edição por subdeck
    const [editingSubdeck, setEditingSubdeck] = useState<{
        deckId: string;
        subdeckId: string;
        config: SubdeckFreeConfig;
    } | null>(null);

    const loadDecks = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const res = await fetch('/api/admin/free-config/decks', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDecks(data.decks);
            }
        } catch {
            onShowToast('Erro ao carregar decks', 'error');
        } finally {
            setLoading(false);
        }
    }, [onShowToast]);

    useEffect(() => { loadDecks(); }, [loadDecks]);

    const toggleExpand = (deckId: string) => {
        setExpandedDecks(prev => {
            const next = new Set(prev);
            next.has(deckId) ? next.delete(deckId) : next.add(deckId);
            return next;
        });
    };

    const handleToggleFreeEntry = async (deck: DeckConfig) => {
        setSaving(`deck-${deck._id}`);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/free-config/decks/${deck._id}/allow-free-entry`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ allowFreeEntry: !deck.allowFreeEntry, isCustom: deck.isCustom })
            });
            if (res.ok) {
                setDecks(prev => prev.map(d =>
                    d._id === deck._id ? { ...d, allowFreeEntry: !d.allowFreeEntry } : d
                ));
                onShowToast(
                    !deck.allowFreeEntry ? 'Deck aberto para navegação gratuita' : 'Deck marcado como premium',
                    'success'
                );
            }
        } catch {
            onShowToast('Erro ao atualizar deck', 'error');
        } finally {
            setSaving(null);
        }
    };

    const openSubdeckEdit = (deckId: string, subdeck: SubdeckConfig) => {
        setEditingSubdeck({
            deckId,
            subdeckId: subdeck._id,
            config: { ...subdeck.freeConfig }
        });
    };

    const saveSubdeckConfig = async (deck: DeckConfig) => {
        if (!editingSubdeck) return;
        setSaving(`subdeck-${editingSubdeck.subdeckId}`);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(
                `/api/admin/free-config/decks/${editingSubdeck.deckId}/subdecks/${editingSubdeck.subdeckId}/free-config`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ ...editingSubdeck.config, isCustom: deck.isCustom })
                }
            );
            if (res.ok) {
                setDecks(prev => prev.map(d => {
                    if (d._id !== editingSubdeck.deckId) return d;
                    return {
                        ...d,
                        subdecks: d.subdecks.map(s =>
                            s._id === editingSubdeck.subdeckId
                                ? { ...s, freeConfig: { ...editingSubdeck.config } }
                                : s
                        )
                    };
                }));
                onShowToast('Amostra configurada com sucesso', 'success');
                setEditingSubdeck(null);
            }
        } catch {
            onShowToast('Erro ao salvar configuração', 'error');
        } finally {
            setSaving(null);
        }
    };

    const filteredDecks = decks.filter(d =>
        !searchQuery.trim() ||
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.cycle?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) return (
        <div className="fca-loading"><div className="fca-spinner" /><p>Carregando...</p></div>
    );

    return (
        <div className="fca-container">
            <div className="fca-header">
                <div>
                    <h3>Configuração de Amostras Gratuitas</h3>
                    <p>Defina quais decks são acessíveis e quantos cards/páginas de apostila são liberados por subdeck</p>
                </div>
            </div>

            <div className="fca-legend">
                <div className="fca-legend-item">
                    <span className="fca-badge fca-badge--premium">Premium</span>
                    <span>Usuário não entra no deck — vê modal de compra</span>
                </div>
                <div className="fca-legend-item">
                    <span className="fca-badge fca-badge--open">Navegável</span>
                    <span>Usuário entra e vê subdecks; bloqueio acontece ao estudar</span>
                </div>
                <div className="fca-legend-item">
                    <span className="fca-badge fca-badge--sample">Com Amostra</span>
                    <span>Subdeck liberado até o limite configurado</span>
                </div>
                <div className="fca-legend-item">
                    <span className="fca-badge fca-badge--locked">Bloqueado</span>
                    <span>Subdeck bloqueado — clicou → modal de compra</span>
                </div>
            </div>

            <div className="fca-search">
                <input
                    type="text"
                    placeholder="Buscar por deck ou ciclo..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="fca-search-input"
                />
            </div>

            <div className="fca-decks-list">
                {filteredDecks.map(deck => {
                    const isExpanded = expandedDecks.has(deck._id);
                    const sampledCount = deck.subdecks.filter(s => s.freeConfig.enabled).length;

                    return (
                        <div key={deck._id} className={`fca-deck-row ${deck.allowFreeEntry ? 'fca-deck--open' : 'fca-deck--premium'}`}>
                            {/* ── Cabeçalho do deck ── */}
                            <div className="fca-deck-header">
                                <button
                                    className="fca-expand-btn"
                                    onClick={() => toggleExpand(deck._id)}
                                >
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>

                                <div className="fca-deck-info">
                                    <div className="fca-deck-title-row">
                                        <span className="fca-deck-name">{deck.name}</span>
                                        {deck.isCustom && <span className="fca-custom-chip">Custom</span>}
                                        {deck.cycle && (
                                            <span className="fca-cycle-chip">{deck.cycle.name}</span>
                                        )}
                                    </div>
                                    <span className="fca-deck-meta">
                                        {deck.subdecks.length} subdecks
                                        {sampledCount > 0 && ` · ${sampledCount} com amostra`}
                                    </span>
                                </div>

                                <div className="fca-deck-actions">
                                    <span className={`fca-badge ${deck.allowFreeEntry ? 'fca-badge--open' : 'fca-badge--premium'}`}>
                                        {deck.allowFreeEntry ? 'Navegável' : 'Premium'}
                                    </span>
                                    <button
                                        className={`fca-toggle-btn ${deck.allowFreeEntry ? 'fca-toggle-btn--disable' : 'fca-toggle-btn--enable'}`}
                                        onClick={() => handleToggleFreeEntry(deck)}
                                        disabled={saving === `deck-${deck._id}`}
                                    >
                                        {deck.allowFreeEntry ? <EyeOff size={14} /> : <Eye size={14} />}
                                        {deck.allowFreeEntry ? 'Tornar Premium' : 'Abrir Navegação'}
                                    </button>
                                </div>
                            </div>

                            {/* ── Subdecks ── */}
                            {isExpanded && (
                                <div className="fca-subdecks">
                                    {!deck.allowFreeEntry && (
                                        <div className="fca-premium-notice">
                                            <Settings size={14} />
                                            Este deck é premium. Ative "Abrir Navegação" para configurar amostras por subdeck.
                                        </div>
                                    )}

                                    {deck.allowFreeEntry && deck.subdecks.map(subdeck => {
                                        const isEditing = editingSubdeck?.subdeckId === subdeck._id
                                            && editingSubdeck?.deckId === deck._id;
                                        const cfg = subdeck.freeConfig;

                                        return (
                                            <div
                                                key={subdeck._id}
                                                className={`fca-subdeck-row ${cfg.enabled ? 'fca-subdeck--sample' : 'fca-subdeck--locked'}`}
                                            >
                                                <div className="fca-subdeck-info">
                                                    <BookOpen size={14} />
                                                    <span className="fca-subdeck-name">{subdeck.name}</span>
                                                    <span className={`fca-badge ${cfg.enabled ? 'fca-badge--sample' : 'fca-badge--locked'}`}>
                                                        {cfg.enabled
                                                            ? `${cfg.freeCardsLimit} cards · ${cfg.freeApostilaPagesLimit} pág.`
                                                            : 'Bloqueado'
                                                        }
                                                    </span>
                                                </div>

                                                {!isEditing ? (
                                                    <button
                                                        className="fca-edit-btn"
                                                        onClick={() => openSubdeckEdit(deck._id, subdeck)}
                                                    >
                                                        <Settings size={13} />
                                                        Configurar
                                                    </button>
                                                ) : (
                                                    <div className="fca-subdeck-edit">
                                                        <label className="fca-edit-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={editingSubdeck.config.enabled}
                                                                onChange={e => setEditingSubdeck(prev => prev
                                                                    ? { ...prev, config: { ...prev.config, enabled: e.target.checked } }
                                                                    : prev
                                                                )}
                                                            />
                                                            <span>Habilitar amostra</span>
                                                        </label>

                                                        {editingSubdeck.config.enabled && (
                                                            <div className="fca-edit-fields">
                                                                <div className="fca-edit-field">
                                                                    <label>Cards gratuitos</label>
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        value={editingSubdeck.config.freeCardsLimit}
                                                                        onChange={e => setEditingSubdeck(prev => prev
                                                                            ? { ...prev, config: { ...prev.config, freeCardsLimit: Number(e.target.value) } }
                                                                            : prev
                                                                        )}
                                                                        className="fca-edit-input"
                                                                    />
                                                                </div>
                                                                <div className="fca-edit-field">
                                                                    <label>Páginas apostila</label>
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        value={editingSubdeck.config.freeApostilaPagesLimit}
                                                                        onChange={e => setEditingSubdeck(prev => prev
                                                                            ? { ...prev, config: { ...prev.config, freeApostilaPagesLimit: Number(e.target.value) } }
                                                                            : prev
                                                                        )}
                                                                        className="fca-edit-input"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="fca-edit-actions">
                                                            <button
                                                                className="fca-save-btn"
                                                                onClick={() => saveSubdeckConfig(deck)}
                                                                disabled={saving === `subdeck-${subdeck._id}`}
                                                            >
                                                                <Check size={13} /> Salvar
                                                            </button>
                                                            <button
                                                                className="fca-cancel-btn"
                                                                onClick={() => setEditingSubdeck(null)}
                                                            >
                                                                <X size={13} /> Cancelar
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FreeConfigAdmin;